import { apiClient } from '@/lib/api/client';
import type { ApiEnvelope } from '@/types/api';
import type {
  InterestAnalytics,
  WalkInitiativeStats,
  WalkParticipant,
  WalkSessionStats,
  SessionParticipantDetail,
  FlaggedSession,
  WalkRewardHistoryEntry,
} from '@/types/analytics';
import type { Partner } from '@/types/utility';
import type { InitiativeEnabler } from '@/types/initiative';

export async function getInterestAnalytics(
  initiativeId: string
): Promise<ApiEnvelope<InterestAnalytics>> {
  return apiClient.get<InterestAnalytics>(
    `/api_partner/initiatives/${initiativeId}/analytics/interest`
  );
}

export async function getWalkStats(
  initiativeId: string
): Promise<ApiEnvelope<WalkInitiativeStats>> {
  return apiClient.get<WalkInitiativeStats>(
    `/api_partner/walk_initiatives/${initiativeId}/statistics`
  );
}

export async function getWalkParticipants(initiativeId: string): Promise<ApiEnvelope<WalkParticipant[]>> {
  return apiClient.get<WalkParticipant[]>(`/api_partner/walk_initiatives/${initiativeId}/participants`);
}

export async function getWalkEnablers(initiativeId: string): Promise<ApiEnvelope<InitiativeEnabler[]>> {
  return apiClient.get<InitiativeEnabler[]>(`/api_partner/walk_initiatives/${initiativeId}/enablers`);
}

export interface PartnersResponse {
  data: Partner[];
  meta?: {
    current_page: string;
    last_page: string;
    per_page: string;
    total: string;
  };
}

export async function getWalkSessionStats(initiativeId: string): Promise<ApiEnvelope<WalkSessionStats>> {
  return apiClient.get<WalkSessionStats>(`/api_partner/walk_initiatives/${initiativeId}/session-statistics`);
}

export async function getWalkParticipantsDetail(
  initiativeId: string,
  params?: { page?: number; per_page?: number; search?: string }
): Promise<ApiEnvelope<SessionParticipantDetail[]>> {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', String(params.page));
  if (params?.per_page) searchParams.set('per_page', String(params.per_page));
  if (params?.search) searchParams.set('search', params.search);
  const qs = searchParams.toString();
  return apiClient.get<SessionParticipantDetail[]>(
    `/api_partner/walk_initiatives/${initiativeId}/participants-detail${qs ? `?${qs}` : ''}`
  );
}

export async function getFlaggedSessions(initiativeId: string): Promise<ApiEnvelope<FlaggedSession[]>> {
  return apiClient.get<FlaggedSession[]>(`/api_partner/walk_initiatives/${initiativeId}/flagged-sessions`);
}

export async function getWalkRewardHistory(
  initiativeId: string,
  params?: { page?: number; per_page?: number }
): Promise<ApiEnvelope<WalkRewardHistoryEntry[]>> {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', String(params.page));
  if (params?.per_page) searchParams.set('per_page', String(params.per_page));
  const qs = searchParams.toString();
  return apiClient.get<WalkRewardHistoryEntry[]>(
    `/api_partner/walk_initiatives/${initiativeId}/reward-history${qs ? `?${qs}` : ''}`
  );
}

export async function getPartners(
  perPage = 15
): Promise<ApiEnvelope<Partner[]>> {
  return apiClient.get<Partner[]>(`/api_partner/get_partners?per_page=${perPage}`);
}
